/**
 * Job Names Constants
 *
 * These constants are used for permission checks across the application.
 * They map to the job names defined in the permission system received from main_fe.
 *
 * Usage:
 *   import { JOB_NAMES } from '../../constants/jobNames';
 *   const canCreate = useCanCreate(JOB_NAMES.PERSONNEL);
 */

export const JOB_NAMES = {
  // Master Data Jobs
  DEPARTMENTS: 'DEPARTMENTS',
  DESIGNATION_MASTER: 'DESIGNATION_MASTER',
  DISTRICTS: 'DISTRICT',
  UNIT_TYPE: 'UNIT_TYPE',
  MINISTRIES: 'MINISTRIES',
  APPS: 'APPS',
  APP_CONFIG: 'APP_CONFIG',

  // Core Jobs
  PERSONNEL: 'PERSONNEL',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  UNITS: 'UNITS',
  UNIT_VILLAGES: 'UNIT_VILLAGES',

  // Auth & RBAC Jobs
  ROLES: 'ROLES',
  SYSTEM_ROLES: 'SYSTEM_ROLES',
  PERMISSIONS: 'PERMISSIONS',
  PERMISSION_MAPPINGS: 'PERMISSION_MAPPINGS',
  USER_ROLE_PERMISSIONS: 'USER_ROLE_PERMISSIONS',
  JOBS: 'JOBS',

  // Prompt Jobs
  PROMPT_MASTER: 'PROMPT_TABLE',
  PROMPT_EXECUTIONS: 'PROMPT_EXECUTIONS',

  // AI Jobs
  AI_MODEL_MASTER: 'AI_MODEL_MASTER',
  AGENT: 'AGENT',
  EVAL_CASE: 'EVAL_CASE',
  FUNCTION_MASTER: 'FUNCTION_MASTER',
  EVALS: 'EVALS',       // the eval SUITE screen (kept id for continuity)
  EVAL_RUNS: 'EVAL_RUN',
  KNOWLEDGE_BASE: 'KNOWLEDGE_BASE',
  PIPELINE: 'PIPELINE',
  PIPELINE_RUNS: 'PIPELINE_RUN',
  RAG: 'RAG',
  CONVERSATIONS: 'CONVERSATIONS',

  // Logging Jobs
  ERROR_MASTER: 'ERROR_MASTER',
  ERROR_LOGS: 'ERROR_LOGS',
  LOG_MASTER: 'LOG_MASTER',
  LOG_TRANSACTION: 'LOG_TRANSACTION',

  // Uploaded files
  FILE_UPLOAD: 'FILE_UPLOAD',

  // Value Sets
  VALUE_SETS: 'VALUE_SETS',

  // Feedback Jobs
  FEEDBACK_MASTER: 'FEEDBACK_MASTER',
  FEEDBACK_SUBMISSION: 'FEEDBACK_SUBMISSION',
  FEEDBACK_REVIEW: 'FEEDBACK_REVIEW',

  // Notification Jobs
  NOTIFICATION_MASTER: 'NOTIFICATION_MASTER',
  NOTIFICATIONS: 'NOTIFICATIONS',

  // Notification Engine — the configuration-driven model. Separate jobs from
  // NOTIFICATION_MASTER because these are separate masters with their own
  // grants: somebody who may author recipients need not be able to edit types.
  NOTIFICATION_TYPE: 'NOTIFICATION_TYPE',
  RECIPIENT_MASTER: 'RECIPIENT_MASTER',
  RECIPIENT_LIST: 'RECIPIENT_LIST',
  ACTION_MASTER: 'ACTION_MASTER',
  SCHEDULE_MASTER: 'SCHEDULE_MASTER',
  NOTIFICATION_RUNS: 'NOTIFICATION_RUNS',

  // Background Jobs
  BACKGROUND_JOB: 'BACKGROUND_JOB',
  BACKGROUND_JOB_RUN: 'BACKGROUND_JOB_RUN',

  // Partner Integration Jobs
  PARTNERS: 'PARTNER',
  PARTNER_CONNECTIONS: 'PARTNER_CONNECTION',
  CONNECTION_TYPES: 'CONNECTION_TYPE',
  // DLT templates live with the gateway account they belong to, so they reuse
  // INTEGRATION_CONFIG rather than minting SMS-specific permission keys —
  // whoever may repoint the gateway is who may change what wording goes out
  // under the organisation's header.
  SMS_TEMPLATES: 'INTEGRATION_CONFIG',
} as const;

// Type for job names
export type JobName = typeof JOB_NAMES[keyof typeof JOB_NAMES];
